import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  LinearProgress,
  FormControl,
  Autocomplete,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const ShowRevaluationDialog = ({ open, onClose, row, showSnackbar }) => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [staffList, setStaffList] = useState([]);
  const [allocations, setAllocations] = useState({}); // { rowId: [{valuation_script_allotment_id, external_staff_id, scriptAllotted}, ...] }
  const [loading, setLoading] = useState(false);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch course data
  useEffect(() => {
    if (open && row) {
      fetchCourseData();
    }
  }, [open, row]);

  // Always start with empty allocations, ignore backend allocations
  /* useEffect(() => {
    if (open && row && rows.length > 0) {
      setAllocations({});
    }
  }, [open, row, rows.length]); */
  useEffect(() => {
    if (open && row && rows.length > 0) {
      fetchExistingAllocations();
    }
  }, [open, row, rows]);
  // Fetch staff list
  useEffect(() => {
    if (open) {
      fetchStaffList();
    }
  }, [open]);

  const fetchCourseData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/exam-revaluation-script-allocation/course-code-student-counts`,
        {
          params: {
            college_id: row.college_id,
            clientId: clientId,
            semester_number: row.semester_number,
            year_number: row.year_number,
            exam_schedule_master_id: row.exam_schedule_master_id,
            valuation_schedule_master_id: row.valuation_schedule_master_id,
            program_id: row.program_id,
            branch_id: row.branch_id,
            regulation_id: row.regulation_id,
          },
        }
      );

      if (response.data.success) {
        const mapped = response.data.data.map((item, index) => ({
          id: index + 1,
          sno: index + 1,
          exam_course_id: item.exam_course_id,
          exam_schedule_master_id: item.exam_schedule_master_id,
          exam_schedule_detail_id: item.exam_schedule_detail_id,
          valuation_schedule_master_id: item.valuation_schedule_master_id,
          course_code: item.course_code,
          course_name: item.course_name || "-",
          branch_id: item.branch_id,
          branch_name: item.branch_name || "-",
          branch_code: item.branch_code || "",
          program_name: item.program_name || "",
          regulation_name: item.regulation_name || "",
          semester_number: item.semester_number,
          year_number: item.year_number,
          student_count: item.student_count || 0,
          college_id: row.college_id,
          allotted_count: item.allotted_count || 0,
          valuated_count: item.valuated_count || 0,
          status: item.status || "Pending",
        }));
        setRows(mapped);
        setFilteredRows(mapped);
      } else {
        setRows([]);
        showSnackbar?.("No courses found", "info");
      }
    } catch (err) {
      console.error("Error fetching course data:", err);
      showSnackbar?.("Failed to fetch course data", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAllocations = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/exam-revaluation-script-allocation/${clientId}/${row.college_id}/${row.exam_schedule_master_id}/${row.valuation_schedule_master_id}/list`
      );

      if (response.data.success && response.data.data.length > 0) {
        const grouped = {};

        // Group allocations by exam_course_id
        response.data.data.forEach((alloc) => {
          const examCourseId = alloc.exam_course_id;
          if (!grouped[examCourseId]) grouped[examCourseId] = [];
          grouped[examCourseId].push({
            valuation_script_allotment_id: alloc.valuation_script_allotment_id,
            external_staff_id: alloc.external_staff_id,
            scriptAllotted: alloc.scripts_allotted,
          });
        });

        // Map exam_course_id → DataGrid row.id
        const allocationMap = {};
        rows.forEach((rowItem) => {
          if (grouped[rowItem.exam_course_id]) {
            allocationMap[rowItem.id] = grouped[rowItem.exam_course_id];
          }
        });

        setAllocations(allocationMap);
      }
    } catch (err) {
      console.error("Error loading existing allocations:", err);
    }
  };

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/staff-external/${clientId}/${row.college_id}/active`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            "If-Modified-Since": "0",
          },
        }
      );

      if (res.data.success) {
        setStaffList(res.data.data || []);
      } else {
        setStaffList([]);
      }
    } catch (err) {
      console.error("Error fetching staff list:", err);
      showSnackbar?.("Error fetching staff list", "error");
    }
  };

  // Search functionality
  useEffect(() => {
    const lower = searchText.toLowerCase().trim();
    if (!lower) {
      setFilteredRows(rows);
      return;
    }
    setFilteredRows(
      rows.filter(
        (rowItem) =>
          (rowItem.course_name && rowItem.course_name.toLowerCase().includes(lower)) ||
          (rowItem.course_code && rowItem.course_code.toLowerCase().includes(lower)) ||
          (rowItem.branch_name && rowItem.branch_name.toLowerCase().includes(lower)) ||
          (rowItem.program_name && rowItem.program_name.toLowerCase().includes(lower))
      )
    );
  }, [searchText, rows]);

  const handleAllocationChange = (rowId, index, key, value) => {
    setAllocations((prev) => {
      const rowAlloc = prev[rowId] ? [...prev[rowId]] : [];
      const current = rowAlloc[index] || {};

      rowAlloc[index] = {
        ...current,
        valuation_script_allotment_id:
          current.valuation_script_allotment_id !== undefined
            ? current.valuation_script_allotment_id
            : null,
        [key]: value,
      };

      return { ...prev, [rowId]: rowAlloc };
    });
  };

  const addNewAllocationRow = (rowId) => {
    setAllocations((prev) => {
      const rowAlloc = prev[rowId] ? [...prev[rowId]] : [];
      rowAlloc.push({
        valuation_script_allotment_id: null,
        external_staff_id: "",
        scriptAllotted: "",
      });
      return { ...prev, [rowId]: rowAlloc };
    });
  };

  const deleteAllocationRow = (rowId, index) => {
    setAllocations((prev) => {
      const updated = [...(prev[rowId] || [])];
      updated.splice(index, 1);
      return {
        ...prev,
        [rowId]: updated.length ? updated : [{ external_staff_id: "", scriptAllotted: "" }],
      };
    });
  };

  const handleConfirmRow = async (rowId) => {
    const rowAlloc = allocations[rowId] || [];
    const currentRow = rows.find((r) => r.id === rowId);
    if (!currentRow) return;

    // Only send filled allocations with course_code
    const filledAlloc = rowAlloc
      .filter((a) => a.external_staff_id && a.scriptAllotted && currentRow.course_code)
      .map((a) => ({
        valuation_script_allotment_id: a.valuation_script_allotment_id ?? null,
        external_staff_id: a.external_staff_id,
        scriptAllotted: a.scriptAllotted,
        course_code: currentRow.course_code,
      }));

    if (filledAlloc.length === 0) {
      showSnackbar?.("Please fill all allocation fields for this course", "warning");
      return;
    }

    const totalAllotted = filledAlloc.reduce(
      (sum, a) => sum + (parseInt(a.scriptAllotted) || 0),
      0
    );
    if (totalAllotted !== currentRow.student_count) {
      showSnackbar?.(
        `Allocation mismatch for ${currentRow.course_code}. Allotted (${totalAllotted}) ≠ Student count (${currentRow.student_count})`,
        "warning"
      );
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/exam-revaluation-script-allocation/confirm`, {
        client_id: clientId,
        college_id: currentRow.college_id,
        exam_course_id: currentRow.exam_course_id,
        exam_schedule_master_id: currentRow.exam_schedule_master_id,
        exam_schedule_detail_id: currentRow.exam_schedule_detail_id,
        valuation_schedule_master_id: currentRow.valuation_schedule_master_id,
        allocations: filledAlloc,
        user_email: user?.user_email,
      });

      showSnackbar?.(
        `${currentRow.course_code} script allocations confirmed successfully`,
        "success"
      );
    } catch (err) {
      let errorMessage = "Failed to confirm allocations";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      showSnackbar?.(`${currentRow.course_code}: ${errorMessage}`, "error");
    }
  };

  const handleConfirmAll = async () => {
    const invalidRows = [];

    const payload = rows.map((currentRow) => {
      // Only send filled allocations with course_code
      const filledAlloc = (allocations[currentRow.id] || [])
        .filter((a) => a.external_staff_id && a.scriptAllotted && currentRow.course_code)
        .map((a) => ({
          valuation_script_allotment_id: a.valuation_script_allotment_id ?? null,
          external_staff_id: a.external_staff_id,
          scriptAllotted: a.scriptAllotted,
          course_code: currentRow.course_code,
        }));

      const totalAllotted = filledAlloc.reduce(
        (sum, a) => sum + (parseInt(a.scriptAllotted) || 0),
        0
      );
      if (totalAllotted !== currentRow.student_count) {
        invalidRows.push(currentRow.course_code);
      }

      return {
        client_id: clientId,
        college_id: currentRow.college_id,
        exam_course_id: currentRow.exam_course_id,
        exam_schedule_master_id: currentRow.exam_schedule_master_id,
        exam_schedule_detail_id: currentRow.exam_schedule_detail_id,
        valuation_schedule_master_id: currentRow.valuation_schedule_master_id,
        allocations: filledAlloc,
        user_email: user?.user_email,
      };
    });

    if (invalidRows.length > 0) {
      showSnackbar?.(`Allocation mismatch for: ${invalidRows.join(", ")}.`, "warning");
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/exam-revaluation-script-allocation/bulk-confirm`,
        payload
      );
      showSnackbar?.("All courses confirmed successfully!", "success");
    } catch (err) {
      console.error("Error in bulk confirmation:", err);
      showSnackbar?.("Bulk confirmation failed.", "error");
    }
  };

  const columns = [
    { field: "sno", headerName: "SNo", flex: 0.3, minWidth: 60 },
    {
      field: "course",
      headerName: "Branch & Course",
      flex: 1.5,
      minWidth: 300,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#1976d2", fontWeight: "bold" }}>
            {params.row.branch_name}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {params.row.course_code}
          </Typography>
          <Typography variant="caption" sx={{ color: "#666" }}>
            {params.row.course_name}
          </Typography>
          {(params.row.program_name || params.row.regulation_name) && (
            <Typography variant="caption" sx={{ color: "#888", mt: 0.5 }}>
              {[params.row.program_name, params.row.regulation_name].filter(Boolean).join(" • ")}
            </Typography>
          )}
        </Box>
      ),
    },

    {
      field: "student_count",
      headerName: "Student Count",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.student_count || 0}
          color="primary"
          variant="outlined"
          size="medium"
          sx={{
            fontWeight: "bold",
            borderRadius: "8px",
            bgcolor: "rgba(223, 80, 168, 0.1)",
          }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {params.row.status}
          </Typography>

          <Typography variant="caption" display="block">
            Allotted: {params.row.allotted_count}
          </Typography>

          <Typography variant="caption" display="block">
            Valuated: {params.row.valuated_count}
          </Typography>
        </Box>
      ),
    },
    {
      field: "allocations",
      headerName: "Staff & Scripts Allotted",
      flex: 2.5,
      minWidth: 500,
      renderCell: (params) => {
        const rowId = params.row.id;
        const rowAlloc = allocations[rowId] || [{ external_staff_id: "", scriptAllotted: "" }];
        const totalAllotted = rowAlloc.reduce(
          (sum, a) => sum + (parseInt(a.scriptAllotted) || 0),
          0
        );
        const isValuated = Number(params.row.valuated_count || 0) > 0;
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", py: 1 }}>
            {rowAlloc.map((alloc, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                {/* Staff Dropdown */}
                <FormControl sx={{ flex: 2, minWidth: 250 }}>
                  <Autocomplete
                    disabled={isValuated}
                    size="medium"
                    fullWidth
                    options={staffList}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.staff_name} — ${option.department || ""} — ${
                            option.college_name || ""
                          }`
                        : ""
                    }
                    value={
                      staffList.find((s) => s.external_staff_id === alloc.external_staff_id) || null
                    }
                    onChange={(event, newValue) => {
                      handleAllocationChange(
                        rowId,
                        index,
                        "external_staff_id",
                        newValue?.external_staff_id || ""
                      );
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {option.staff_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {[option.department, option.college_name].filter(Boolean).join(" • ")}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(paramsInput) => (
                      <TextField
                        {...paramsInput}
                        size="medium"
                        disabled={isValuated}
                        label="Select Staff"
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    )}
                  />
                </FormControl>

                {/* Allotted Input */}
                <TextField
                  label="Scripts"
                  type="number"
                  size="medium"
                  disabled={isValuated}
                  value={alloc.scriptAllotted}
                  onChange={(e) => {
                    let value = parseInt(e.target.value) || 0;
                    const otherTotal = totalAllotted - (parseInt(alloc.scriptAllotted) || 0);
                    const maxAllowed = params.row.student_count - otherTotal;

                    if (value > maxAllowed) {
                      showSnackbar?.(
                        `Maximum ${maxAllowed} scripts allowed for this staff`,
                        "warning"
                      );
                      value = maxAllowed;
                    }

                    handleAllocationChange(rowId, index, "scriptAllotted", value);
                  }}
                  sx={{
                    width: 90,
                    "& .MuiInputBase-input": {
                      fontWeight: "bold",
                    },
                    "& .MuiInputLabel-root": {
                      fontWeight: "bold",
                    },
                  }}
                  inputProps={{ min: 0, max: params.row.student_count }}
                />

                {/* Delete Button */}
                {rowAlloc.length > 1 && (
                  <Button
                    size="medium"
                    color="error"
                    disabled={isValuated}
                    variant="outlined"
                    onClick={() => deleteAllocationRow(rowId, index)}
                    sx={{ minWidth: 36, width: 36, height: 36, p: 0 }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            ))}

            {/* Action Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              {!isValuated && totalAllotted < params.row.student_count && (
                <Button
                  size="medium"
                  variant="outlined"
                  disabled={isValuated}
                  onClick={() => addNewAllocationRow(rowId)}
                  sx={{
                    backgroundColor: "#379de6ff",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#0e216fff" },
                  }}
                >
                  + Add Staff
                </Button>
              )}
              {totalAllotted === params.row.student_count && totalAllotted > 0 && (
                <Button
                  size="medium"
                  variant="contained"
                  disabled={isValuated}
                  color="success"
                  onClick={() => handleConfirmRow(params.row.id)}
                  sx={{ color: "white" }}
                >
                  ✅ Confirm
                </Button>
              )}
              {totalAllotted > 0 && totalAllotted < params.row.student_count && (
                <Typography variant="caption" color="warning.main">
                  {params.row.student_count - totalAllotted} scripts remaining
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }

        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Allot Revaluation Scripts</Typography>
        {row && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Semester: {row.semester_number} | Year: {row.year_number || "N/A"}
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="medium"
            label="Search by Course Code, Name, or Branch"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Type to search..."
          />
        </Box>

        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <LinearProgress sx={{ width: "50%" }} />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="textSecondary">
              {rows.length === 0
                ? "No courses available for the selected semester/year."
                : "No matching courses found."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              pagination
              disableRowSelectionOnClick
              loading={loading}
              getRowHeight={() => "auto"}
              onCellClick={(params, event) => event.stopPropagation()}
              onRowClick={(params, event) => event.stopPropagation()}
              sx={{
                "& .MuiDataGrid-cell": {
                  py: 1,
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirmAll}
          disabled={loading || filteredRows.length === 0}
          sx={{
            backgroundColor: "#19d272ff",
            color: "#fff",
            "&:hover": { backgroundColor: "#15b05eff" },
            "&.Mui-disabled": {
              backgroundColor: "#e0e0e0",
            },
          }}
        >
          ✅ Confirm All
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={loading}
          sx={{
            color: "#f0e7e7",
            borderColor: "#d32f2f",
            "&:hover": {
              borderColor: "#b71c1c",
              backgroundColor: "#b71c1c",
              color: "#f0e7e7",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ShowRevaluationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.shape({
    college_id: PropTypes.number,
    semester_number: PropTypes.number,
    year_number: PropTypes.number,
    exam_schedule_master_id: PropTypes.number,
    valuation_schedule_master_id: PropTypes.number,
    program_id: PropTypes.number,
    branch_id: PropTypes.number,
    regulation_id: PropTypes.number,
  }),
  showSnackbar: PropTypes.func,
};

export default ShowRevaluationDialog;
